import React, { useState } from "react";
import { api, formatError } from "../api";
import { Download, AlertTriangle, ShieldCheck } from "lucide-react";

/* ─────────────────────────────────────────────────────────────────────────
 *  <MyDataPanel /> — surfaces user-side data ownership controls.
 *
 *  Two actions:
 *    1. Download my data  → calls GET /api/me/export, saves a single JSON
 *       file the user can keep / hand to a third party.
 *    2. Close my account  → confirms twice, then anonymises PII and logs
 *       the user out.
 *
 *  Copy is intentionally neutral — no regulatory acronyms surfaced anywhere.
 *  Looks like a normal account-control card.
 * ──────────────────────────────────────────────────────────────────────── */

export default function MyDataPanel() {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const download = async () => {
    setBusy(true); setMsg(""); setErr("");
    try {
      const r = await api.get("/me/export");
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const ts = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `skillconnect-my-data-${ts}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      setMsg("Downloaded ✓");
      setTimeout(() => setMsg(""), 2500);
    } catch (e) { setErr(formatError(e)); }
    finally { setBusy(false); }
  };

  const close = async () => {
    const ok1 = window.confirm(
      "Close your SkillConnect account?\n\n"
      + "We will remove your name, email, phone and other personal details. "
      + "Your historical interview and application records will be kept "
      + "in anonymised form so employer history stays consistent.\n\n"
      + "This action cannot be undone."
    );
    if (!ok1) return;
    const ok2 = window.prompt("Type 'CLOSE' (in capitals) to confirm.") === "CLOSE";
    if (!ok2) return;
    setBusy(true); setErr("");
    try {
      await api.post("/me/close-account");
      // Backend cleared the cookie; route to the login page.
      window.location.assign("/login?closed=1");
    } catch (e) { setErr(formatError(e)); setBusy(false); }
  };

  return (
    <div className="card card-pad" data-testid="my-data-panel">
      <div className="flex items-start gap-3">
        <ShieldCheck size={18} className="text-signal mt-0.5 shrink-0" />
        <div className="flex-1">
          <h3 className="font-display font-bold">Your data</h3>
          <p className="text-sm text-muted mt-1">
            You control your information. Download a copy anytime, or close
            your account if you no longer want to use SkillConnect.
          </p>
        </div>
      </div>

      <div className="mt-4 grid sm:grid-cols-2 gap-3">
        <button
          type="button"
          onClick={download}
          disabled={busy}
          className="btn-ghost justify-center"
          data-testid="my-data-download"
        >
          <Download size={14} /> {busy ? "Preparing…" : "Download my data"}
        </button>
        <button
          type="button"
          onClick={close}
          disabled={busy}
          className="btn-ghost justify-center text-red-600 hover:bg-red-50"
          data-testid="my-data-close"
        >
          <AlertTriangle size={14} /> Close my account
        </button>
      </div>

      {msg && <div className="text-xs text-green-700 mt-3" data-testid="my-data-msg">{msg}</div>}
      {err && <div className="text-xs text-red-600 mt-3" data-testid="my-data-err">{err}</div>}
    </div>
  );
}
